import { useState, useRef } from 'react';

export default function BeforeAfter() {
  const [sliderPosition, setSliderPosition] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const handleMove = (clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  const handleMouseDown = () => {
    isDragging.current = true;
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current) return;
    handleMove(e.clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    handleMove(e.touches[0].clientX);
  };

  const handleClick = (e: React.MouseEvent) => {
    handleMove(e.clientX);
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Rénovation de Phares
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Redonnez de l'éclat à vos phares ternis ! Glissez le curseur pour voir la transformation
          </p>
          <div className="mt-4 inline-flex items-center bg-[#0EA5E9]/10 text-[#0EA5E9] px-4 py-2 rounded-full font-semibold">
            <i className="ri-lightbulb-flash-line mr-2"></i>
            50€ la paire
          </div>
        </div>

        <div className="max-w-3xl mx-auto">
          <div
            ref={containerRef}
            className="relative w-full h-96 md:h-[500px] rounded-2xl overflow-hidden shadow-2xl cursor-ew-resize select-none"
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onMouseMove={handleMouseMove}
            onTouchMove={handleTouchMove}
            onClick={handleClick}
          >
            {/* Image Après (fond) */}
            <div className="absolute inset-0 w-full h-full">
              <img
                src="https://static.readdy.ai/image/04f309692b2e018ede3a87223bcd45f5/14d18243d8d4590abb5af89948f261d0.jpeg"
                alt="Phare après rénovation"
                className="w-full h-full object-cover object-center"
                draggable={false}
              />
              <div className="absolute bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg font-semibold text-sm shadow-lg">
                <i className="ri-check-line mr-1"></i>
                APRÈS
              </div>
            </div>

            {/* Image Avant (superposée avec clip) */}
            <div
              className="absolute inset-0 w-full h-full overflow-hidden"
              style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
            >
              <img
                src="https://static.readdy.ai/image/04f309692b2e018ede3a87223bcd45f5/a1370ccaf7c707d8b3c7e608056ec037.jpeg"
                alt="Phare avant rénovation"
                className="w-full h-full object-cover object-center"
                draggable={false}
              />
              <div className="absolute bottom-4 left-4 bg-gray-700 text-white px-4 py-2 rounded-lg font-semibold text-sm shadow-lg">
                <i className="ri-close-line mr-1"></i>
                AVANT
              </div>
            </div>

            {/* Slider */}
            <div
              className="absolute top-0 bottom-0 w-1 bg-white shadow-lg"
              style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
            >
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center">
                <i className="ri-arrow-left-right-line text-xl text-[#0EA5E9]"></i>
              </div>
            </div>
          </div>

          <p className="text-center text-gray-500 mt-4 text-sm">
            <i className="ri-hand-coin-line mr-1"></i>
            Glissez le curseur de gauche à droite pour comparer
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div className="bg-gray-50 rounded-xl p-6 text-center">
            <div className="w-12 h-12 bg-[#0EA5E9]/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="ri-eye-line text-xl text-[#0EA5E9]"></i>
            </div>
            <h4 className="font-semibold text-gray-900 mb-1">Meilleure Visibilité</h4>
            <p className="text-sm text-gray-600">Conduite de nuit plus sûre</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-6 text-center">
            <div className="w-12 h-12 bg-[#0EA5E9]/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="ri-shield-check-line text-xl text-[#0EA5E9]"></i>
            </div>
            <h4 className="font-semibold text-gray-900 mb-1">Contrôle Technique</h4>
            <p className="text-sm text-gray-600">Évitez la contre-visite</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-6 text-center">
            <div className="w-12 h-12 bg-[#0EA5E9]/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="ri-money-euro-circle-line text-xl text-[#0EA5E9]"></i>
            </div>
            <h4 className="font-semibold text-gray-900 mb-1">Économique</h4>
            <p className="text-sm text-gray-600">Moins cher qu'un remplacement</p>
          </div>
        </div>
      </div>
    </section>
  );
}
